import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { List, X, CaretDown } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/investments", label: "Investments" },
  { to: "/real-estate", label: "Real Estate" },
  { to: "/agriculture", label: "Agriculture" },
  { to: "/insights", label: "Insights" },
  { to: "/events", label: "Events" },
  { to: "/partnerships", label: "Partnerships" },
];

const MayotoLogo = () => (
  <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
    <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#0a1128]">
      <span className="text-[#00b4d8] font-black text-xl leading-none font-display">M</span>
    </span>
    <span className="font-display font-black text-xl tracking-tight text-[#0a1128]">MAYOTO</span>
  </Link>
);

export default function Navbar() {
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background,box-shadow,border] duration-200 ${
        scrolled ? "bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm" : "bg-white/70 backdrop-blur border-b border-transparent"
      }`}
    >
      <div className="container-wide flex h-20 items-center justify-between">
        <MayotoLogo />

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g,"-")}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive ? "text-[#00b4d8]" : "text-slate-700 hover:text-[#0a1128]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-user-menu"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1.5 text-sm hover:border-[#00b4d8]"
                >
                  <span className="h-7 w-7 rounded-full bg-[#0a1128] text-white grid place-items-center text-xs font-bold">
                    {(user.full_name || "M").slice(0,1).toUpperCase()}
                  </span>
                  <span className="max-w-[120px] truncate">{user.full_name || user.email}</span>
                  <CaretDown size={14} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white">
                <DropdownMenuItem onClick={() => nav("/dashboard")} data-testid="nav-dashboard-link">Investor Dashboard</DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => nav("/admin")} data-testid="nav-admin-link">Admin Console</DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="nav-logout">Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login">
                <Button variant="ghost" className="text-slate-700 hover:text-[#0a1128]">Login</Button>
              </Link>
              <Link to="/register" data-testid="nav-get-started">
                <Button className="btn-shine bg-[#00b4d8] hover:bg-[#0096c7] text-white rounded-full px-5">Get Started</Button>
              </Link>
            </>
          )}
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-md border border-slate-200"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <List size={20} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200">
          <div className="container-wide py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mnav-link-${n.label.toLowerCase().replace(/\s+/g,"-")}`}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-base font-medium ${isActive ? "text-[#00b4d8] bg-slate-50" : "text-slate-700"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="pt-3 flex gap-2">
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setOpen(false)} className="flex-1">
                    <Button variant="outline" className="w-full">Dashboard</Button>
                  </Link>
                  <Button className="flex-1 bg-[#0a1128] text-white" onClick={async () => { await logout(); setOpen(false); nav("/"); }}>Log out</Button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="flex-1">
                    <Button variant="outline" className="w-full">Login</Button>
                  </Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="flex-1">
                    <Button className="w-full bg-[#00b4d8] hover:bg-[#0096c7] text-white">Get Started</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
